import React from 'react';
import styled from 'styled-components/macro';
import Isvg from 'react-inlinesvg';
import Slider from 'react-slick';
import { rgba } from 'polished';
import translate from './TranslateHOC';
import navigate_next from '../img/icons/navigate_next.svg';
import navigate_before from '../img/icons/navigate_before.svg';
import { IsvgWrapper } from '../helpers';

const handleKeyboardClick = (event, onClick) => {
  if ((event.charCode === 32 || event.charCode) === 13 && onClick) {
    onClick(event);
  }
};

const getTabIndex = (className) => {
  return className.includes('slick-disabled') ? -1 : 0;
};

const LeftNavButton = ({ className, onClick, label }) => {
  return (
    <CardRowArrowLeft
      aria-label={label}
      className={`${className}`}
      onClick={onClick}
      onKeyPress={(event) => handleKeyboardClick(event, onClick)}
      tabIndex={getTabIndex(className)}
      role="button"
    >
      <IsvgWrapper>
        <Isvg src={navigate_before} />
      </IsvgWrapper>
    </CardRowArrowLeft>
  );
};

const RightNavButton = ({ className, onClick, label }) => {
  return (
    <CardRowArrowRight
      aria-label={label}
      className={`${className}`}
      onClick={onClick}
      onKeyPress={(event) => handleKeyboardClick(event, onClick)}
      tabIndex={getTabIndex(className)}
      role="button"
    >
      <IsvgWrapper>
        <Isvg src={navigate_next} />
      </IsvgWrapper>
    </CardRowArrowRight>
  );
};

const Dots = ({ dots }) => {
  return <CardRowDots className="slick-dots">{dots}</CardRowDots>;
};

function CustomSlider({
  className,
  slidesToScrollDefault,
  slidesToShowDefault,
  responsiveSettings,
  strings,
  children,
}) {
  const slickChange = () => {
    //disable tabbing for all slides initially
    const allSlides = document.querySelectorAll('.slick-slide');
    for (const slide of allSlides) {
      const linkEl = slide.getElementsByTagName('a')[0];
      if (linkEl) {
        linkEl.tabIndex = -1;
      }
    }
    //enable tabbing for slides in view
    const activeSlides = document.querySelectorAll('.slick-active');
    for (const slide of activeSlides) {
      const linkEl = slide.getElementsByTagName('a')[0];
      if (linkEl) {
        linkEl.removeAttribute('tabindex');
      }
    }
  };

  const settings = {
    onInit: slickChange,
    afterChange: slickChange,
    onLazyLoad: slickChange,
    variableWidth: false,
    slidesToScroll: slidesToScrollDefault,
    slidesToShow: slidesToShowDefault,
    responsive: responsiveSettings,
    dots: true,
    lazyLoad: 'progressive',
    infinite: false,
    appendDots: (dots) => <Dots dots={dots} />,
    prevArrow: <LeftNavButton label={strings.back} />,
    nextArrow: <RightNavButton label={strings.next} />,
  };

  return (
    <SliderContainer>
      <Slider className={className} {...settings}>
        {children}
      </Slider>
    </SliderContainer>
  );
}

const SliderContainer = styled.div`
  .card-slick {
    margin: 0;
    padding-bottom: 0px;
    .slick-slide {
      margin: 12px 0 0;
      position: relative;
      .card {
        margin-left: 6px;
        margin-right: 6px;
        width: calc(100% - 12px);
      }
    }
    .slick-slide[aria-hidden='true'] > div:first-of-type {
      cursor: default;
      opacity: 0.3;
      pointer-events: none;
    }
    .slick-list {
      margin: 0 12px;
      width: calc(100% - 24px);
      padding-bottom: 0;
    }
    .slick-track {
      margin-left: unset;
      margin-right: unset;
    }
  }

  @media screen and (min-width: 768px) {
    .card-slick .slick-list {
      margin: 0;
      width: calc(100% - 10px);
    }
  }

  @media screen and (min-width: 1275px) {
    .card-slick .slick-list {
      width: 100%;
    }
    .card-slick {
      margin: 0 12px 0 0;
    }
  }

  .slick-list {
    overflow: visible;
    // padding-bottom: 18px;
  }
`;

const CardRowArrow = styled.div`
  background: ${(props) => props.theme.color.primary};
  background: linear-gradient(
    266.92deg,
    ${(props) => props.theme.color.primary75} 0%,
    ${(props) => props.theme.color.primaryFull} 100%
  );
  box-shadow: 0 4px 12px 0
    rgba(lighten(${(props) => props.theme.color.primaryFull}, 10), 0);
  cursor: pointer;
  height: 12px;
  padding: 8px 16px;
  position: absolute;
  transform: translateY(0);
  top: -35px;
  width: 12px;

  &:hover,
  &:focus {
    background: ${(props) => props.theme.color.primaryFull};
    box-shadow: 0 4px 12px 0
      rgba(darken(${(props) => props.theme.color.primaryFull}, 10), 0.6);
    transform: translateY(0);
  }

  &:focus {
    box-shadow: inset 0 0 0 2px rgba(0, 103, 244, 0.5),
      0 4px 12px 0 rgba(darken($primaryFull, 10), 0.6);
    outline: 2px solid
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.statusLightFocus
          : props.theme.color.statusDarkFocus};
    outline-offset: 2px;
  }

  &:active {
    box-shadow: 0 4px 12px 0
      rgba(lighten(${(props) => props.theme.color.white50}, 10), 0);
    transform: translateY(0);
  }

  &:not(.slick-disabled) {
    box-shadow: 0px 2px 4px rgba(110, 79, 28, 0.1),
      0px 8px 16px ${(props) => props.theme.color.primary20};
  }

  &.slick-disabled {
    background: $primary;
    opacity: 0.25;
    cursor: default;
  }

  &.slick-disabled:hover,
  &.slick-disabled:focus {
    box-shadow: 0 4px 12px 0
      rgba(lighten(${(props) => props.theme.color.white50}, 10), 0);
    transform: translateY(-0);
  }

  &.slick-disabled:focus {
    box-shadow: inset 0 0 0 2px rgba(0, 103, 244, 0.5);
  }

  @media screen and (min-width: 768px) {
    display: block;
  }

  svg {
    height: 100%;
    width: 100%;
    g * {
      fill: ${(props) => props.theme.color.blackFull};
      fill-opacity: 1;
    }
  }

  .isvg {
    height: 12px;
    position: absolute;
    width: 12px;
  }
`;

const CardRowArrowLeft = styled(CardRowArrow)`
  border-radius: 50px 0 0 50px;
  padding-right: 12px;
  left: auto;
  right: 58px;
  @media screen and (min-width: 1275px) {
    right: 47px;
  }
  .isvg {
    margin: 0 0 0 -1px;
  }
  &:before,
  &:after {
    display: none;
  }
`;

const CardRowArrowRight = styled(CardRowArrow)`
  border-radius: 0 50px 50px 0;
  padding-left: 12px;
  right: 15px;
  @media screen and (min-width: 1275px) {
    right: 4px;
  }
  .isvg {
    margin: 0 0 0 1px;
  }
  &:before,
  &:after {
    display: none;
  }
`;

const CardRowDots = styled.ul`
  bottom: -25px;
  display: block;
  list-style: none;
  margin: 0;
  padding: 0;
  position: absolute;
  text-align: center;
  width: 100%;

  display: none !important;
  height: 40px;
  position: absolute;
  right: 98px;
  top: -32px;
  width: auto;
  @media screen and (min-width: 768px) {
    display: block !important;
  }
  @media screen and (min-width: 1275px) {
    right: 88px;
  }

  li button:hover:before,
  li {
    margin: 0;
    button:before {
      background: ${(props) => props.theme.color.white50};
      border-radius: 100%;
      content: '';
      display: block;
      height: 10px;
      opacity: 1;
      transform: transition(all 0.1s ease-in);
      width: 10px;
    }
  }

  li button:before {
    background: ${(props) =>
      props.theme.name === 'light'
        ? rgba(props.theme.color.blackFull, 0.25)
        : props.theme.color.white40};
    border-radius: 100%;
    content: '';
    display: block;
    height: 10px;
    opacity: 1;
    transition: all 0.1s ease-in;
    width: 10px;
  }

  li button:hover:before,
  li button:focus:before {
    background: ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.black50
        : props.theme.color.white75};
  }

  li button:focus:before {
    outline: 2px solid
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.statusLightFocus
          : props.theme.color.statusDarkFocus};
    outline-offset: 2px;
  }

  li.slick-active button:hover:before,
  li.slick-active button:focus:before,
  li.slick-active button:before {
    background: ${(props) => props.theme.color.primaryFull} !important;
    opacity: 1;
  }
`;

export default translate(CustomSlider);
